import { Fragment, useEffect, useState } from 'react';
import { Nullable, SelectOption } from 'global';
import { formatCurrency } from 'utils';
import { calculateMarkupFlat, calculateMarkupPercent } from 'utils/financeCommissionCalculations';
import { AftermarketProductCategory } from '@trnsact/trnsact-shared-types';
import { Add, CheckCircle, Info, Remove, Security, Warning } from '@mui/icons-material';
import { Path, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Box, Button, CircularProgress, Collapse, Divider, IconButton, Stack, Typography } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { AutocompleteField, CurrencyInputField, InterestInputField } from 'components/form';

const CATEGORY_CONFIG: Array<{ key: AftermarketProductCategory; label: string }> = [
  { key: AftermarketProductCategory.ServiceContract, label: 'Service Contract' },
  { key: AftermarketProductCategory.Insurance, label: 'Insurance' },
  { key: AftermarketProductCategory.Gap, label: 'GAP' },
  { key: AftermarketProductCategory.Roadside, label: 'Roadside' },
  { key: AftermarketProductCategory.None, label: 'Other' },
];

export interface CategoryAdditionals {
  markupFlat: Nullable<number>;
  markupPercent: Nullable<number>;
  totalCost: Nullable<number>;
}

export interface RevenueProductData {
  id: string;
  name: string;
  dateSold: string;
  cost: number;
  markupFlat: number;
  markupPercent: number;
  category: string | null;
}

export interface RevenueCardFormValues {
  proposal: Nullable<SelectOption>;
  products: RevenueProductData[];
  categoryAdditionals: Partial<Record<AftermarketProductCategory, CategoryAdditionals>>;
}

export interface AftermarketProposalData {
  totals: { markupFlat: number };
  products: RevenueProductData[];
}

export interface AftermarketRevenueCardProps {
  isOptionsLoading: boolean;
  proposalListOptions: SelectOption[];
  onCreateProductQuote: VoidFunction;
  isProposalRevenueDataLoading: boolean;
  getRevenueCardInitialFormValues: () => Promise<RevenueCardFormValues>;
  handleGetProposal: (proposalId: string) => Promise<Nullable<AftermarketProposalData>>;
  onFormStateChange?: (formValues: RevenueCardFormValues, isDirty: boolean) => void;
}

const EMPTY_ADDITIONALS: CategoryAdditionals = { markupFlat: null, markupPercent: null, totalCost: null };

export const DEFAULT_CATEGORY_ADDITIONALS: Partial<Record<AftermarketProductCategory, CategoryAdditionals>> = {
  [AftermarketProductCategory.ServiceContract]: { ...EMPTY_ADDITIONALS },
  [AftermarketProductCategory.Insurance]: { ...EMPTY_ADDITIONALS },
  [AftermarketProductCategory.Gap]: { ...EMPTY_ADDITIONALS },
  [AftermarketProductCategory.Roadside]: { ...EMPTY_ADDITIONALS },
  [AftermarketProductCategory.None]: { ...EMPTY_ADDITIONALS },
};

export const RevenueCard = ({
  isOptionsLoading,
  handleGetProposal,
  onCreateProductQuote,
  proposalListOptions = [],
  isProposalRevenueDataLoading,
  getRevenueCardInitialFormValues,
  onFormStateChange,
}: AftermarketRevenueCardProps) => {
  const [expandedCategories, setExpandedCategories] = useState<Partial<Record<AftermarketProductCategory, boolean>>>({
    [AftermarketProductCategory.ServiceContract]: true,
    [AftermarketProductCategory.Insurance]: true,
    [AftermarketProductCategory.Gap]: true,
    [AftermarketProductCategory.Roadside]: true,
    [AftermarketProductCategory.None]: true,
  });

  const { control, setValue, getValues, formState } = useForm<RevenueCardFormValues>({
    defaultValues: getRevenueCardInitialFormValues,
  });

  const { fields } = useFieldArray({ control, name: 'products' });
  const { isDirty } = formState;
  const proposalValue = useWatch({ control, name: 'proposal' });
  const productsValue = useWatch({ control, name: 'products' });
  const categoryAdditionalsValue = useWatch({ control, name: 'categoryAdditionals' });

  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(getValues(), isDirty);
    }
  }, [proposalValue, productsValue, categoryAdditionalsValue, isDirty, onFormStateChange, getValues]);

  const toggleCategory = (key: AftermarketProductCategory) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleReset = () => {
    setValue('categoryAdditionals', DEFAULT_CATEGORY_ADDITIONALS, { shouldDirty: false });
  };

  const totalMarkup = CATEGORY_CONFIG.reduce((sum, { key }) => {
    const isNoneCategory = key === AftermarketProductCategory.None;
    const productMarkup = fields
      .filter(f => (isNoneCategory ? !f.category || f.category === key : f.category === key))
      .reduce((s, f) => s + (f.markupFlat || 0), 0);
    const additionalMarkup = categoryAdditionalsValue?.[key]?.markupFlat ?? 0;
    return sum + productMarkup + additionalMarkup;
  }, 0);

  const totalAdditionalProductCost = CATEGORY_CONFIG.reduce((sum, { key }) => {
    return sum + (categoryAdditionalsValue?.[key]?.totalCost ?? 0);
  }, 0);

  return (
    <Box className="revenueCard">
      <RenderWithCondition
        condition={isOptionsLoading || !!proposalListOptions.length}
        fallBack={
          <Stack alignItems="center" justifyContent="center" gap="0.5rem">
            <Info color="disabled" sx={{ fontSize: '3rem' }} />
            <Typography>There is no Accepted proposals for this VO</Typography>
          </Stack>
        }
      >
        <Box className="revenueCardHeader">
          <Box className="revenueCardHeaderIconWithTitle">
            <Box className="revenueCardHeaderIcon">
              <Security />
            </Box>

            <Typography variant="subtitle1" component="span">
              Protection Products
            </Typography>
          </Box>

          <Button variant="outlined" size="small" onClick={handleReset}>
            Reset
          </Button>
        </Box>

        <Box className="revenueAftermarketContent">
          <AutocompleteField
            name="proposal"
            control={control}
            isLoading={isOptionsLoading}
            options={proposalListOptions}
            disabled={!proposalListOptions.length}
            extraAction={async nextValue => {
              if (nextValue?.value) {
                const response = await handleGetProposal(nextValue.value);
                setValue('products', response?.products ?? []);
              }
            }}
            textFieldProps={{
              size: 'small',
              label: 'Proposal',
              variant: 'outlined',
              placeholder: 'Search proposal...',
            }}
          />

          <RenderWithCondition condition={!!proposalValue}>
            <RenderWithFallback
              isLoading={isProposalRevenueDataLoading}
              fallBack={
                <Box className="revenueAftermarketLoading">
                  <CircularProgress />
                  <Typography>Loading products...</Typography>
                </Box>
              }
            >
              <Stack gap="0.5rem" sx={{ mt: 1 }}>
                {CATEGORY_CONFIG.map(({ key, label }) => {
                  const isNoneCategory = key === AftermarketProductCategory.None;
                  const categoryFieldsWithIndex = fields
                    .map((field, index) => ({ field, index }))
                    .filter(({ field }) =>
                      isNoneCategory ? !field.category || field.category === key : field.category === key
                    );
                  const hasProducts = categoryFieldsWithIndex.length > 0;
                  const isExpanded = expandedCategories[key] ?? true;
                  const productMarkupTotal = categoryFieldsWithIndex.reduce(
                    (sum, { field }) => sum + (field.markupFlat || 0),
                    0
                  );
                  const productCostTotal = categoryFieldsWithIndex.reduce(
                    (sum, { field }) => sum + (field.cost || 0),
                    0
                  );
                  const additionalMarkup = categoryAdditionalsValue?.[key]?.markupFlat ?? 0;
                  const additionalCost = categoryAdditionalsValue?.[key]?.totalCost ?? 0;
                  const categoryTotal = productMarkupTotal + additionalMarkup;
                  const categoryProductCost = productCostTotal + additionalCost;
                  const categoryMarkupPercent = calculateMarkupPercent(categoryTotal, categoryProductCost);
                  const hasCategoryData = hasProducts || additionalMarkup > 0 || additionalCost > 0;
                  const productCostValue = categoryAdditionalsValue?.[key]?.totalCost;
                  const isMarkupPercentDisabled = !productCostValue;

                  return (
                    <Box key={key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1,
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleCategory(key)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <IconButton size="small">
                            {isExpanded ? <Remove fontSize="small" /> : <Add fontSize="small" />}
                          </IconButton>
                          <Typography variant="subtitle2">{label}</Typography>
                        </Box>

                        {hasProducts ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Warning color="warning" fontSize="small" />
                        )}
                      </Box>

                      <Collapse in={isExpanded}>
                        <Box sx={{ px: 2, pb: 2 }}>
                          {!hasProducts && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                py: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: 1,
                                  bgcolor: 'grey.100',
                                  flex: 1,
                                }}
                              >
                                <Warning color="warning" fontSize="small" />
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    No products sold via Trnsact
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    You can create a product quote or manually enter revenue from external services
                                  </Typography>
                                </Box>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={onCreateProductQuote}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                Create Product Quote
                              </Button>
                            </Box>
                          )}

                          {categoryFieldsWithIndex.map(({ field, index }) => (
                            <Fragment key={field.id}>
                              <Box className="revenueAftermarketProductsListItem">
                                <Box className="productInfoSection">
                                  <Typography variant="body2" color="textDisabled">
                                    Date Sold: {field.dateSold}
                                  </Typography>
                                  <Typography variant="body1">{field.name}</Typography>
                                </Box>

                                <Box className="productPriceSection">
                                  <CurrencyInputField
                                    control={control}
                                    label="Markup ($)"
                                    name={`products.${index}.markupFlat`}
                                    textFieldProps={{ variant: 'outlined', disabled: true }}
                                  />
                                  <InterestInputField
                                    control={control}
                                    label="Markup (%)"
                                    name={`products.${index}.markupPercent`}
                                    textFieldProps={{ variant: 'outlined', disabled: true }}
                                  />
                                </Box>
                              </Box>
                            </Fragment>
                          ))}

                          {(hasProducts || hasCategoryData) && <Divider sx={{ my: 1 }} />}

                          {hasProducts && (
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                              Additional Products
                            </Typography>
                          )}

                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr 1fr',
                              gap: '0.5rem',
                            }}
                          >
                            <CurrencyInputField
                              control={control}
                              label="Product Cost"
                              name={`categoryAdditionals.${key}.totalCost`}
                              textFieldProps={{ variant: 'outlined', size: 'small' }}
                              customOnChange={nextValue => {
                                setValue(
                                  `categoryAdditionals.${key}.totalCost` as Path<RevenueCardFormValues>,
                                  nextValue,
                                  { shouldDirty: true }
                                );
                                const markupFlat = getValues(`categoryAdditionals.${key}.markupFlat`) ?? 0;
                                setValue(
                                  `categoryAdditionals.${key}.markupPercent` as Path<RevenueCardFormValues>,
                                  calculateMarkupPercent(markupFlat, nextValue),
                                  { shouldDirty: true }
                                );
                              }}
                            />
                            <CurrencyInputField
                              control={control}
                              label="Markup ($)"
                              name={`categoryAdditionals.${key}.markupFlat`}
                              textFieldProps={{ variant: 'outlined', size: 'small' }}
                              customOnChange={nextValue => {
                                setValue(
                                  `categoryAdditionals.${key}.markupFlat` as Path<RevenueCardFormValues>,
                                  nextValue,
                                  { shouldDirty: true }
                                );
                                const productCost = getValues(`categoryAdditionals.${key}.totalCost`) ?? 0;
                                if (productCost > 0) {
                                  setValue(
                                    `categoryAdditionals.${key}.markupPercent` as Path<RevenueCardFormValues>,
                                    calculateMarkupPercent(nextValue, productCost),
                                    { shouldDirty: true }
                                  );
                                }
                              }}
                            />
                            <InterestInputField
                              control={control}
                              label="Markup (%)"
                              name={`categoryAdditionals.${key}.markupPercent`}
                              textFieldProps={{ variant: 'outlined', size: 'small', disabled: isMarkupPercentDisabled }}
                              customOnChange={nextValue => {
                                setValue(
                                  `categoryAdditionals.${key}.markupPercent` as Path<RevenueCardFormValues>,
                                  nextValue,
                                  { shouldDirty: true }
                                );
                                const productCost = getValues(`categoryAdditionals.${key}.totalCost`) ?? 0;
                                setValue(
                                  `categoryAdditionals.${key}.markupFlat` as Path<RevenueCardFormValues>,
                                  calculateMarkupFlat(productCost, nextValue),
                                  { shouldDirty: true }
                                );
                              }}
                            />
                          </Box>

                          {(hasProducts || hasCategoryData) && <Divider sx={{ my: 1 }} />}

                          {hasCategoryData && (
                            <>
                              <Box className="revenueAftermarketTotal">
                                <Typography color="textDisabled">Total Markup</Typography>
                                <Typography>
                                  {formatCurrency(categoryTotal, true)}
                                  {(hasProducts || !isMarkupPercentDisabled) && (
                                    <>&nbsp;&nbsp;{categoryMarkupPercent.toFixed(2)}%</>
                                  )}
                                </Typography>
                              </Box>
                              <Divider />
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Stack>
            </RenderWithFallback>

            <Divider />

            <Box sx={{ display: 'flex', gap: '0.5rem' }}>
              <Box className="revenueAftermarketTotal" sx={{ flex: 1 }}>
                <Typography color="textDisabled">Total Markup</Typography>

                <RenderWithFallback
                  isLoading={isProposalRevenueDataLoading}
                  fallBack={<Typography>Calculating...</Typography>}
                >
                  <Typography>{formatCurrency(totalMarkup, true)}</Typography>
                </RenderWithFallback>
              </Box>

              <Box className="revenueAftermarketTotal" sx={{ flex: 1 }}>
                <Typography color="textDisabled">Total Product Cost</Typography>

                <RenderWithFallback
                  isLoading={isProposalRevenueDataLoading}
                  fallBack={<Typography>Calculating...</Typography>}
                >
                  <Typography>{formatCurrency(totalAdditionalProductCost, true)}</Typography>
                </RenderWithFallback>
              </Box>
            </Box>
          </RenderWithCondition>
        </Box>
      </RenderWithCondition>
    </Box>
  );
};
